<template lang="pug">
dv-tooltip(
  :disabled="!showTimezone"
  :max-width="1000"
  :text="currentMSKTime"
  v-bind="extendAttrs.tooltip"
)
  d-input-base.d-datetime-field(
    :component="VDatetimeField"
    v-bind="extendAttrs"
    v-on="$listeners"
    v-model="inputValue"
  )
    template(
      v-for="(_, name) in $scopedSlots"
      v-slot:[name]="data"
    )
      slot(:name="name" v-bind="data")
    template(
      v-for="(_, name) in $slots"
      :slot="name"
    )
      slot(:name="name")
  slot(name="message" v-bind="{ message }")
    v-expand-transition
      p.ui-body-2.mt-2(v-if="message && !hideDetails")
        v-icon.d-inline-block.mr-1(
          v-if="hasError"
          :size="16"
          color="ui-error"
        ) {{ errorIcon }}
        span(:class="{ 'ui-error--text': hasError }") {{ typeof message === 'object' ? message.message : message }}
  //-   template(#dateFieldLabel)
  //-     slot(name="labelDate")
  //-   template(#timeFieldLabel)
  //-     slot(name="labelTime")

</template>

<script>
import { VDatetimeField } from 'v-datetime-field';
import DInputBase from '../DInputBase.vue';
import DvTooltip from '../../tooltips/DvTooltip.vue';
import { addHours } from 'date-fns';
import { readableDateWithTime } from './helpers';
import { serverDateDefault } from '@/plugins/date-print';

export default {
  components: {
    DvTooltip,
    DInputBase,
  },
  props: {
    value: { type: String, default: null },
    max: { type: String, default: null },
    min: { type: String, default: null },
    fieldAttrs: { type: Object, default: () => ({}) },
    showTimezone: { type: Boolean, default: false },
    errorIcon: { type: [String, Object], default: '$ui-attention' },
    errorMessages: { type: Array, default: () => ([]) },
    error: { type: Boolean, default: false },
    successMessages: { type: Array, default: () => ([]) },
    hideDetails: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      VDatetimeField,
    };
  },
  computed: {
    hasError() {
      return this.error || !!this.errorMessages?.length;
    },
    message() {
      const [first] = [...(this.errorMessages || []), ...(this.successMessages || [])];
      return first || null;
    },
    extendFieldAttrs() {
      return {
        error: this.hasError,
        hideDetails: true,
        errorMessages: null,
        outlined: true,
        solo: true,
        flat: true,
        dense: true,
        class: [
          'd-input-base',
          'rounded-lg',
          ...this.hasError ? ['error--text'] : [],
        ],
        fullWidth: true,
        color: 'ui-primary',
        height: 48,
        ...this.fieldAttrs,
      };
    },
    extendAttrs() {
      const { $attrs, disabled, timePickerBounds } = this;
      const { min: minTime, max: maxTime } = timePickerBounds;
      const { minDate, maxDate } = this.datePickerBounds || {};
      return {
        tooltip: {},
        hideDetails: true,
        buttonColor: 'ui-primary',
        class: [
          ...this.hasError ? ['d-datetime-field--error'] : [],
        ],
        componentClass: [
          ...this.hasError ? ['error--text'] : [],
        ],
        disabled,
        ...$attrs,
        dateProps: {
          placeholder: 'дд.мм.гггг',
          autocomplete: 'off',
          appendIcon: '$ui-calendar-days',
          disabled,
          ...this.extendFieldAttrs,
          ...($attrs?.dateProps || {}),
        },
        timeProps: {
          placeholder: '00:00',
          autocomplete: 'off',
          appendIcon: '$ui-time',
          disabled,
          ...this.extendFieldAttrs,
          ...($attrs?.timeProps || {}),
        },
        datePickerProps: {
          min: minDate,
          max: maxDate,
          color: 'ui-primary',
          disabled,
          ...($attrs?.datePickerProps || {}),
        },
        timePickerProps: {
          color: 'ui-primary',
          headerColor: 'ui-neutral lighten-2',
          disabled,
          min: minTime,
          max: maxTime,
          ...($attrs?.timePickerProps || {}),
        },
      };
    },
    inputValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    currentTimezone() {
      const timezone = new Date().toString().match(/([A-Z]+[+-][0-9]+.*)/)[1];
      return `Часовой пояс: ${timezone.replace('GMT', 'UTC').replace(/0/g, '')}`;
    },
    currentMSKTime() {
      const { inputValue, currentTimezone } = this;
      if (!inputValue) return currentTimezone;
      return this.getMSKTime(inputValue);
    },
    timePickerBounds() {
      const extractTime = (dateTime) => {
        if (!dateTime || typeof dateTime !== 'string') return undefined;

        const [datePart, timePart] = dateTime.trim().split(/\s+/);
        if (!datePart || !timePart) return undefined;

        return timePart;
      };

      return {
        min: extractTime(this.min),
        max: extractTime(this.max),
      };
    },
    datePickerBounds() {
      const extractDate = (dateTime) => {
        if (!dateTime || typeof dateTime !== 'string') return undefined;
        const [datePart] = dateTime.trim().split(/\s+/);
        return datePart || undefined;
      };

      return {
        minDate: extractDate(this.min),
        maxDate: extractDate(this.max),
      };
    },
  },
  methods: {
    getMSKTime(time) {
      const mskTimeOffset = 3; // Московское время
      const UTCtime = serverDateDefault(time);
      const mskTime = readableDateWithTime(addHours(new Date(UTCtime), mskTimeOffset));
      return `Московское время (UTC+3): ${mskTime}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.d-datetime-field {
  &::v-deep .v-datetime-field {
    &__wrapper {
      gap: 8px;
      justify-content: space-between;
      margin-top: 0px;
    }
    &__time,
    &__date {
      flex: 1;
      margin-left: 0 !important;
    }
  }
  &::v-deep .v-input__icon.v-input__icon--append {
    & .v-icon__component {
      color: #{$ui-primary} !important;
    }
  }
  &::v-deep .error--text {
    color: #{$ui-error} !important;
  }
  &--error {
    &::v-deep .v-input:not(.error--text) {
      & > .v-input__control > .v-input__slot > fieldset {
        color: #{$ui-error} !important;
      }
    }
  }
}
</style>
